import { forwardRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

type NotificationBellTriggerProps = {
  unreadCount: number;
  label: string;
  triggerDataCy: string;
  badgeDataCy: string;
};

/**
 * Bell icon + unread badge used by NotificationDropdown (authenticated users +
 * OTP guests). forwardRef + prop spread let it sit inside a Radix
 * `<DropdownMenuTrigger asChild>`, which clones this child and forwards its ref
 * and trigger props onto the Button.
 */
export const NotificationBellTrigger = forwardRef<HTMLButtonElement, NotificationBellTriggerProps>(
  ({ unreadCount, label, triggerDataCy, badgeDataCy, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      className="relative mr-4 -ml-2 rounded-md hover:border-none hover:bg-transparent! focus-visible:ring-0"
      data-cy={triggerDataCy}
      aria-label={label}
      tooltip={label}
      icon={Bell}
      iconClassName="stroke-icon-base"
      {...props}
    >
      {unreadCount > 0 && (
        <Badge
          data-cy={badgeDataCy}
          className="absolute top-px left-5 justify-center font-bold"
          variant="failure"
          size="md"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}
    </Button>
  ),
);

NotificationBellTrigger.displayName = 'NotificationBellTrigger';
